/** Plain-language rendering of events and their fields, shared by history, review and detail pages. */
import { ANIMAL_CLASS, ENTITY_FIELDS, ENTITY_LABELS, UNIT_LABELS, type EventDTO, type Field, type Fields, type Formatters, type ProposalItem, type SubjectRef } from "../../contract";
import { formatDate } from "../lib/format";
import { t } from "../lib/i18n";
import { registry } from "../registry";

export function eventSummary(e: { type: string; data: Record<string, unknown>; subjects: SubjectRef[]; occurred_at?: string | null }, fmt: Formatters): string {
  const def = registry.event(e.type);
  if (!def) return e.type;
  try {
    return def.summary({ type: e.type, data: e.data as never, subjects: e.subjects.map((s) => ({ ...s, label: s.label ?? fmt.label(s.id) })), occurred_at: e.occurred_at }, fmt);
  } catch {
    return t(def.label);
  }
}

export function formatValue(field: Field, value: unknown, fmt: Formatters): string {
  if (value === undefined || value === null || value === "") return "";
  switch (field.kind) {
    case "boolean": return value ? t("Yes") : t("No");
    case "date": return formatDate(String(value), field.withTime ? "minute" : "date");
    case "enum": return Array.isArray(value) ? fmt.list(value.map((v) => t(field.optionLabels[v] ?? v))) : t(field.optionLabels[String(value)] ?? String(value));
    case "quantity": return fmt.quantity(value as { value: number; unit: string });
    case "money": return fmt.money(value as { minor: number; currency: string });
    case "ref": return Array.isArray(value) ? fmt.list(value.map((v) => fmt.label(String(v)))) : fmt.label(String(value));
    case "number": return `${value}${field.suffix ? ` ${t(field.suffix)}` : ""}`;
    case "object": return Object.entries(field.fields).map(([k, f]) => formatValue(f, (value as Record<string, unknown>)[k], fmt)).filter(Boolean).join(", ");
    case "list": return Array.isArray(value) ? value.map((v) => formatValue(field.of, v, fmt)).filter(Boolean).join("; ") : "";
    default: return String(value);
  }
}

/** Label/value pairs for every filled field, for detail views. */
export function fieldRows(fields: Fields, data: Record<string, unknown>, fmt: Formatters): Array<{ key: string; label: string; value: string }> {
  return Object.entries(fields).map(([key, f]) => ({ key, label: t(f.label), value: formatValue(f, data[key], fmt) })).filter((r) => r.value);
}

export function DataList({ fields, data, fmt }: { fields: Fields; data: Record<string, unknown>; fmt: Formatters }) {
  const rows = fieldRows(fields, data, fmt);
  if (!rows.length) return null;
  return <dl className="data-list">{rows.map((r) => <div key={r.key}><dt>{r.label}</dt><dd>{r.value}</dd></div>)}</dl>;
}

export function subjectLine(e: Pick<EventDTO, "subjects">, fmt: Formatters, max = 4): string {
  const animals = e.subjects.filter((s) => s.kind === "animal");
  if (animals.length > max) return t("{{count}} animals", { count: animals.length });
  return fmt.list(e.subjects.map((s) => s.label ?? fmt.label(s.id)));
}

/** One line for a proposal item, in the reader's language. */
export function itemTitle(item: ProposalItem, fmt: Formatters): { icon: string; title: string; detail: string } {
  switch (item.op) {
    case "event": {
      const def = registry.event(item.type);
      return { icon: def?.icon ?? "note", title: def ? t(def.label) : item.type, detail: eventSummary({ type: item.type, data: item.data, subjects: item.subjects, occurred_at: item.occurred.value }, fmt) };
    }
    case "create": {
      const fields = ENTITY_FIELDS[item.entity];
      const bits = Object.entries(fields).filter(([k]) => k !== "name" && k !== "tag").map(([k, f]) => formatValue(f, item.data[k], fmt)).filter(Boolean).slice(0, 4);
      const name = (item.data.tag ?? item.data.name ?? "") as string;
      return { icon: item.entity === "animal" ? "calf" : item.entity === "place" ? "place" : item.entity === "contact" ? "contact" : "plus", title: t("New {{thing}}", { thing: t(ENTITY_LABELS[item.entity]).toLowerCase() }), detail: [name, ...bits].filter(Boolean).join(" · ") };
    }
    case "update": {
      const fields = ENTITY_FIELDS[item.entity];
      const changes = Object.entries(item.data).map(([k, v]) => (fields[k] ? `${t(fields[k].label)}: ${formatValue(fields[k], v, fmt) || t("cleared")}` : k));
      return { icon: "edit", title: t("Change {{thing}} {{label}}", { thing: t(ENTITY_LABELS[item.entity]).toLowerCase(), label: fmt.label(item.id) }), detail: changes.join(" · ") };
    }
    case "task":
      return { icon: "task", title: t("Task"), detail: [item.data.title, item.data.due_at ? t("due {{date}}", { date: formatDate(item.data.due_at) }) : null, item.data.assignee_id ? fmt.label(item.data.assignee_id) : null].filter(Boolean).join(" · ") };
    case "job": {
      const def = registry.jobs.get(item.type);
      return { icon: def?.icon ?? "work", title: t("Start {{job}}", { job: def ? t(def.label).toLowerCase() : item.type }), detail: [item.title, item.subjects?.length ? t("{{count}} animals", { count: item.subjects.length }) : null].filter(Boolean).join(" · ") };
    }
    case "membership":
      return { icon: "group", title: t("Update group {{group}}", { group: fmt.label(item.group_id) }), detail: [item.add?.length ? t("Add {{count}}", { count: item.add.length }) : null, item.remove?.length ? t("Remove {{count}}", { count: item.remove.length }) : null].filter(Boolean).join(" · ") };
    case "stock":
      return { icon: "stock", title: t("Adjust stock"), detail: `${fmt.label(item.item_id)} ${item.delta > 0 ? "+" : ""}${item.delta} ${t(UNIT_LABELS[item.unit] ?? item.unit)}` };
    case "vocabulary":
      return { icon: "notebook", title: t("Learn a word"), detail: item.term };
  }
}

export const classLabel = (c: string) => t(ANIMAL_CLASS[c as keyof typeof ANIMAL_CLASS] ?? c);
